/**
 * Synchronously determines the orientation of an already loaded image
 * @param img The HTMLImageElement to check
 * @returns 'portrait', 'landscape', or 'square' based on image dimensions
 */
export function checkImageOrientation(img: HTMLImageElement): string {
  if (img.naturalHeight > img.naturalWidth) {
    return 'portrait';
  } else if (img.naturalHeight === img.naturalWidth) {
    return 'square';
  } else {
    return 'landscape';
  }
}

/**
 * Determines the orientation of an image from its URL.
 * @param imageUrl The URL of the image to check.
 * @returns A Promise that resolves with 'portrait', 'landscape', or 'square', or rejects with an error.
 */
export const getImageOrientation = (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Basic check for image extension in the URL
    const imageExtensionRegex = /\.(jpeg|jpg|gif|png|webp|svg|bmp|ico)(\?.*)?$/i;
    if (!imageExtensionRegex.test(imageUrl)) {
      reject(new Error('URL does not appear to be an image based on its extension.'));
      return;
    }

    const img = new Image();

    img.onload = () => {
      let orientation;
      if (img.naturalHeight > img.naturalWidth) {
        orientation = 'portrait';
      } else if (img.naturalHeight === img.naturalWidth) {
        orientation = 'square';
      } else {
        orientation = 'landscape';
      }
      resolve(orientation);
    };

    img.onerror = () => {
      reject(new Error('Could not load image from URL to determine orientation.'));
    };

    img.src = imageUrl;
  });
};

export function setStylingForImage(parentCard: HTMLLIElement, image: HTMLImageElement, orientation: string): void {
  // Get container dimensions
  const { width: cardWidth, height: cardHeight } = parentCard.getBoundingClientRect();
  
  // Set base properties for all images to prevent stretching
  image.style.objectFit = 'contain';
  image.style.maxWidth = '100%';
  image.style.maxHeight = '100%';
  // Check if likely on mobile (using viewport width as indicator)
  const isMobileView = window.innerWidth < 768;
  if(isMobileView){
    parentCard.style.display = 'flex';
    parentCard.style.justifyContent = 'center';
    parentCard.style.alignItems = 'center';
  }
  
  // Determine if we should prioritize width or height based on image and container
  const shouldPrioritizeHeight = determineHeightPriority(cardWidth, cardHeight, orientation, image);
  
  // Apply the appropriate styling based on priority
  if (shouldPrioritizeHeight) {
    if (isMobileView && orientation === 'portrait') {
      applyMobilePortraitStyling(image, cardWidth, cardHeight);
    } else {
      image.style.height = '100%';
      image.style.width = 'auto';
    }
  } else {
    image.style.width = '100%';
    image.style.height = 'auto';
  }
}

function applyMobilePortraitStyling(image: HTMLImageElement, cardWidth: number, cardHeight: number): void {
  // For portrait images on mobile, calculate appropriate width percentage
  // based on the aspect ratios of both the container and image
  const imgWidth = image.naturalWidth || image.width || 400; // Default to 400 if not available
  const imgHeight = image.naturalHeight || image.height || 600; // Default to 600 if not available
  
  // Calculate optimal width as a percentage
  // This preserves the aspect ratio while ensuring the image fits properly
  const heightRatio = cardHeight / imgHeight;
  const calculatedWidth = imgWidth * heightRatio;
  const widthPercentage = (calculatedWidth / cardWidth) * 100;
  
  // Apply the calculated percentage with a small buffer
  image.style.width = `${Math.min(widthPercentage, 100)}%`;
  image.style.height = '100%';
}

// Helper function to determine if we should prioritize height over width
function determineHeightPriority(
  cardWidth: number, 
  cardHeight: number, 
  orientation: string, 
  image: HTMLImageElement
): boolean {
  // Make sure we have valid dimensions
  const imgWidth = image.naturalWidth || image.width || 0;
  const imgHeight = image.naturalHeight || image.height || 0;
  
  if (imgWidth === 0 || imgHeight === 0) {
    // If image dimensions aren't available yet, use the orientation string as fallback
    return orientation === 'portrait' || orientation === 'square';
  }
  
  // Calculate aspect ratios
  const cardRatio = cardWidth / cardHeight;
  const imageRatio = imgWidth / imgHeight;
  
  // First check: compare aspect ratios
  // If card is wider (relative to its height) than the image, prioritize height
  // If image is wider (relative to its height) than the card, prioritize width
  if (cardRatio > imageRatio) {
    // Card is relatively wider than image (or image is relatively taller)
    return true; // prioritize height
  } else {
    // Image is relatively wider than card (or card is relatively taller)
    return false; // prioritize width
  }
}

export function restructureImages(photoGalleryUl: HTMLUListElement): void {
  if(!photoGalleryUl?.children?.length) {
    return;
  }
  const photoGalleryList = Array.from(photoGalleryUl.children) as HTMLLIElement[];
  photoGalleryList.forEach((photoGalleryLi, index) => {
    const img = photoGalleryLi.querySelector(".gallery-card img") as HTMLImageElement;
    const card = photoGalleryLi.querySelector('.card-img') as HTMLDivElement;
    const cardBody = photoGalleryLi.querySelector('.card-body') as HTMLDivElement;
    if (card) {
      card.style.display = 'flex';
      card.style.justifyContent = 'center';
      card.style.alignItems = 'center';
    }
    if(cardBody) {
      cardBody.style.textAlign = 'center';
    }
    if(img) {
      try {
        // Check if the image is already loaded
        if (img.complete && img.naturalHeight !== 0) {
          // Can determine orientation synchronously using our helper function
          const orientation = checkImageOrientation(img);
          setStylingForImage( photoGalleryLi, img, orientation);
        } else {
          // If not loaded, we still need to use the async approach
          getImageOrientation(img.src)
            .then((orientation) => {
              setStylingForImage( photoGalleryLi, img, orientation);
            })
            .catch((error) => {
              console.warn(`Photo gallery image resizer issue. Error determining orientation for image at index ${index}:`, error);
            });
        }
      } catch (error) {
        console.warn(`Error processing image at index ${index}:`, error);
      }
    }
  });
}


/**
 * Applies inline styles to the image based on orientation.
 * - Landscape: width 100%, height auto
 * - Portrait: height 100%, width auto, centered
 */
function setStylingForImageV2(image: HTMLImageElement, orientation: any): void {
  
  image.style.objectFit = 'contain';
  image.style.display = 'block';

  if (orientation === 'portrait') {
    image.style.width = 'auto';
    image.style.height = '100%';
  } else if (orientation === 'landscape') {
    // For landscape and square, width 100%, height auto
    image.style.width = '100%';
    image.style.height = 'auto';
  }
}

/**
 * Attaches error handling to ensure component doesn't crash and logs errors for image loading.
 */
function attachErrorHandler(image: HTMLImageElement, imgLabel: string, liIndex: number): void {
  image.onerror = () => {
    image.style.display = 'none'; // Hide broken image
    // You can replace this with any logging/reporting mechanism you have
    console.warn(
      `[Carousel] Failed to load ${imgLabel} image at index ${liIndex}: ${image.src}`
    );
    // Optionally, display a fallback or error message here
  };
}

export function restructureImagesV2(carouselUl: HTMLUListElement): void {
  if (!carouselUl?.children?.length) {
    return;
  }
  const carouselLis = Array.from(carouselUl.children) as HTMLLIElement[];
  carouselLis.forEach((li, liIndex) => {
    // Find .carousel-wrap-img inside each li
    const imgWrap = li.querySelector('.carousel-wrap-img') as HTMLDivElement;
    if (!imgWrap) return;
    imgWrap.style.alignItems = 'center';

    // Find <img-comparison-slider> inside .carousel-wrap-img
    const slider = imgWrap.querySelector('img-comparison-slider');
    if (!slider) return;

    // Handle both images in the slider (before and after)
    const beforeImg = slider.querySelector('figure.before-img img');
    const afterImg = slider.querySelector('figure.after-img img');

    [beforeImg, afterImg].forEach((img, imgIdx) => {
      if (!img) return;
      attachErrorHandler(img as HTMLImageElement, imgIdx === 0 ? 'before' : 'after', liIndex);

      // Handler to set correct style once image is loaded
      const handleLoaded = () => {
        try {
          const orientation = checkImageOrientation(img as HTMLImageElement);
          setStylingForImageV2(img as HTMLImageElement, orientation);
          // Optionally, log success with dimensions and orientation
          // console.log(`[Carousel] Loaded ${imgIdx === 0 ? 'before' : 'after'} image at index ${liIndex}: ${orientation}, ${img.naturalWidth}x${img.naturalHeight}`);
        } catch (e) {
          // Defensive: never crash on error
          console.warn(
            `[Carousel] Exception on styling image at index ${liIndex}:`,
            e
          );
        }
      };

      if ((img as HTMLImageElement)?.complete && (img as HTMLImageElement).naturalHeight > 0) {
        handleLoaded();
      } else {
        (img as HTMLImageElement).onload = handleLoaded;
      }
    });
  });
}